import React, { useState, useEffect, useCallback } from "react";
import styles from "./multipleChoiceModal.module.css";
import axios from "axios";
import { addNewLearningTool } from "../../../utilities/learningZonesUtils";

const MultipleChoiceModal = ({
  header,
  unitContent,
  approveBtnText,
  cancelBtnText,
  show,
  onClose,
  onConfirm,
  learningToolId,
  learningZoneId,
}) => {
  /* The multiple choice modal is used to enter details of a new multiple choice unit, as well as to edit an existing multiple choice unit. */

  const [initialState, setInitialState] = useState({
    questionText: "",
    solutions: [],
    correctSolutionIdx: 0,
  });

  const [questionText, setQuestionText] = useState("");
  const [solutions, setSolutions] = useState([]);
  const [correctSolutionIdx, setCorrectSolutionIdx] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const [duplicateError, setDuplicateError] = useState("");

  useEffect(() => {
    if (unitContent && show) {
      const correctSolutionIdx = unitContent.unitSolutions.findIndex(
        (solution) => solution.solution_text === unitContent.correct_answer
      );

      const initialState = {
        questionText: unitContent.question_text,
        solutions: JSON.parse(JSON.stringify(unitContent.unitSolutions)), // Deep copy to prevent mutation
        correctSolutionIdx: correctSolutionIdx !== -1 ? correctSolutionIdx : 0,
      };

      setInitialState(initialState);
      setQuestionText(initialState.questionText);
      setSolutions(initialState.solutions);
      setCorrectSolutionIdx(initialState.correctSolutionIdx);
    } else {
      setQuestionText("");
      setSolutions([]);
      setCorrectSolutionIdx(0);
    }
  }, [unitContent, show]);

  /**
   * Validate the question and solutions entered by the user
   */
  const validateForm = useCallback(() => {
    const uniqueSolutions = new Set(
      solutions.map((s) => s.solution_text.trim())
    );
    if (uniqueSolutions.size !== solutions.length) {
      setDuplicateError("Duplicate solutions are not allowed.");
      setIsValid(false);
      return;
    } else {
      setDuplicateError("");
    }

    if (
      questionText.trim() !== "" &&
      solutions.length >= 2 &&
      correctSolutionIdx !== null &&
      solutions.every((solution) => solution.solution_text.trim() !== "")
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [questionText, solutions, correctSolutionIdx]);

  useEffect(() => {
    validateForm();
  }, [questionText, solutions, correctSolutionIdx, validateForm]);

  /**
   * Handle change in the question text
   */
  const handleQuestionChange = (e) => {
    setQuestionText(e.target.value);
  };

  /**
   * Handle change in one of the solutions
   * @param {*} index - integer - the index of the changed solution
   * @param {*} value - string - the new value of the solution
   */
  const handleSolutionChange = (index, value) => {
    const newSolutions = [...solutions];
    newSolutions[index].solution_text = value;
    setSolutions(newSolutions);
  };

  /**
   * Add a solution to the unit
   */
  const handleAddSolution = () => {
    setSolutions([...solutions, { solution_text: "" }]);
  };

  /**
   * Remove a solution from the unit
   * @param {*} index - the index of the removed solution
   */
  const handleRemoveSolution = (index) => {
    const newSolutions = solutions.filter((_, i) => i !== index);
    setSolutions(newSolutions);
    if (correctSolutionIdx === index) {
      setCorrectSolutionIdx(null);
    } else if (correctSolutionIdx > index) {
      setCorrectSolutionIdx(correctSolutionIdx - 1);
    }
  };

  /**
   * Add a new unit to the tool
   */
  const handleNewUnit = async () => {
    let toolId = learningToolId;

    if (!toolId) {
      /* The tool does not exist yet, and should be created first - this will be the first unit of the tool */
      try {
        toolId = await addNewLearningTool(learningZoneId, "multipleChoice");
      } catch (error) {
        console.error(error.message);
        return;
      }
    }

    const newUnitContent = {
      learningToolId: toolId,
      question_text: questionText,
      unitSolutions: solutions,
      correct_solution: solutions[correctSolutionIdx]?.solution_text,
      correct_answer: solutions[correctSolutionIdx]?.solution_text,
    };

    try {
      const reqUrl = `/learning_units/create/multiple_choice`;

      const response = await axios.post(reqUrl, {
        learningToolId: toolId,
        questionText: newUnitContent.question_text,
        correctAnswer: newUnitContent.correct_solution,
        unitSolutions: newUnitContent.unitSolutions.map((solution, index) => ({
          solutionText: solution.solution_text,
          solutionNumberInUnit: index + 1,
        })),
      });

      if (response.status === 200 || response.status === 201) {
        newUnitContent.learning_unit_id = response.data.learning_unit_id;

        onConfirm({
          learning_tool_id: toolId,
          tool_type_id: "multipleChoice",
          newUnit: {
            learning_unit_id: newUnitContent.learning_unit_id,
            question_text: questionText,
            correct_answer: newUnitContent.correct_solution,
            unitSolutions: newUnitContent.unitSolutions.map(
              (solution, index) => ({
                solution_text: solution.solution_text,
                solution_number_in_unit: index + 1,
                is_active: 1,
                learning_unit_id: newUnitContent.learning_unit_id,
              })
            ),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_active: 1,
            learning_tool_id: learningToolId,
            unit_type: "multipleChoice",
          },
        });
      } else {
        console.error("Failed to add the new learning unit.");
      }
    } catch (error) {
      console.error("Failed to add the new learning unit.");
    }

    onClose();
  };

  /**
   * Update the database and the page with the updated content of a unit.
   */
  const handleConfirm = async () => {
    const updatedUnitContent = {
      question_text: questionText,
      unitSolutions: solutions,
      correct_solution: solutions[correctSolutionIdx]?.solution_text,
      correct_answer: solutions[correctSolutionIdx]?.solution_text,
    };

    try {
      const learningUnitId = unitContent.learning_unit_id;
      const reqUrl = `/learning_units/update/multiple_choice/${learningUnitId}`;

      const response = await axios.put(reqUrl, {
        learningToolId,
        questionText: updatedUnitContent.question_text,
        correctAnswer: updatedUnitContent.correct_solution,
        unitSolutions: updatedUnitContent.unitSolutions.map(
          (solution, index) => ({
            solutionText: solution.solution_text,
            solutionNumberInUnit: index + 1,
          })
        ),
      });

      if (response.status === 200) {
        onConfirm({
          ...updatedUnitContent,
          correct_answer: updatedUnitContent.correct_solution,
        });
      } else {
        console.error("Failed to update the learning unit.");
      }
    } catch (error) {
      console.error("Failed to update the learning unit.");
    }

    onClose();
  };

  /**
   * Reset the modal text input fields and error message
   */
  const resetState = () => {
    setQuestionText(initialState.questionText);
    setSolutions(initialState.solutions);
    setCorrectSolutionIdx(initialState.correctSolutionIdx);
    setDuplicateError("");
  };

  /**
   * Close the modal when clicking outside it
   */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      resetState();
      onClose();
    }
  };

  /* Close the modal */
  const handleClose = () => {
    resetState();
    onClose();
  };

  /* Handle clicking the cancel button of the modal */
  const handleCancel = () => {
    resetState();
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={handleClose}>
          &times;
        </button>
        <h2>{header}</h2>
        <div className={styles.field}>
          <label>Question Text</label>
          <input
            type="text"
            value={questionText}
            onChange={handleQuestionChange}
          />
        </div>
        <div className={styles.field}>
          <label>Solutions</label>
          <div className={styles.solutionsHeader}>
            <span>Solution Text</span>
            <span style={{ marginLeft: "auto" }}>Correct Solution</span>
          </div>
          {solutions.map((solution, index) => (
            <div key={index} className={styles.solutionField}>
              <input
                type="text"
                value={solution.solution_text}
                onChange={(e) => handleSolutionChange(index, e.target.value)}
              />
              <button onClick={() => handleRemoveSolution(index)}>
                Remove
              </button>
              <input
                type="radio"
                name="correctSolution"
                checked={correctSolutionIdx === index}
                onChange={() => setCorrectSolutionIdx(index)}
                className={styles.correctSolutionRadio}
              />
            </div>
          ))}
          <button onClick={handleAddSolution}>Add Solution</button>
        </div>
        {duplicateError && <p className={styles.error}>{duplicateError}</p>}
        <div className={styles.actions}>
          <button onClick={handleCancel}>{cancelBtnText}</button>
          {unitContent && (
            <button onClick={handleConfirm} disabled={!isValid}>
              {approveBtnText}
            </button>
          )}

          {!unitContent && (
            <button onClick={handleNewUnit} disabled={!isValid}>
              {approveBtnText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MultipleChoiceModal;
